import { useCallback } from "react";

// Sistema de logging para a aplicação
// Em produção, os logs são removidos automaticamente pelo Vite

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: unknown;
  timestamp: Date;
  component?: string;
  userId?: string;
  sessionId?: string;
}

interface LogConfig {
  enableConsole: boolean;
  enableRemote: boolean;
  remoteEndpoint?: string;
  maxLogSize: number;
  logLevel: LogLevel;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private config: LogConfig;
  private logBuffer: LogEntry[] = [];
  private sessionId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.config = {
      enableConsole: this.isDevelopment,
      enableRemote: false, // Pode ser habilitado para produção
      maxLogSize: 1000,
      logLevel: this.isDevelopment ? "debug" : "warn",
    };
  }

  private generateSessionId(): string {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3,
    };
    return levels[level] >= levels[this.config.logLevel];
  }

  private sanitizeData(data: unknown): unknown {
    if (typeof data === "object" && data !== null) {
      const sanitized: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(
        data as Record<string, unknown>,
      )) {
        // Remover dados sensíveis
        if (
          ["password", "token", "secret", "key"].some((sensitive) =>
            key.toLowerCase().includes(sensitive),
          )
        ) {
          sanitized[key] = "[REDACTED]";
        } else {
          sanitized[key] = this.sanitizeData(value);
        }
      }
      return sanitized;
    }
    return data;
  }

  private log(
    level: LogLevel,
    message: string,
    data?: unknown,
    component?: string,
    userId?: string,
  ) {
    if (!this.shouldLog(level)) return;

    const entry: LogEntry = {
      level,
      message,
      data: this.sanitizeData(data),
      timestamp: new Date(),
      component,
      userId,
      sessionId: this.sessionId,
    };

    // Adicionar ao buffer
    this.logBuffer.push(entry);
    if (this.logBuffer.length > this.config.maxLogSize) {
      this.logBuffer.shift();
    }

    if (this.config.enableConsole) {
      const prefix = component ? `[${component}]` : "[App]";

      switch (level) {
        case "debug":
          console.debug(`${prefix} ${message}`, data || "");
          break;
        case "info":
          console.info(`${prefix} ${message}`, data || "");
          break;
        case "warn":
          console.warn(`${prefix} ${message}`, data || "");
          break;
        case "error":
          console.error(`${prefix} ${message}`, data || "");
          break;
      }
    }

    // Em produção, pode enviar para serviço de logging remoto
    if (this.config.enableRemote && this.config.remoteEndpoint) {
      this.sendToRemote(entry).catch(() => {
        // Silenciosamente falhar se não conseguir enviar
      });
    }
  }

  private async sendToRemote(entry: LogEntry): Promise<void> {
    if (!this.config.remoteEndpoint) return;

    try {
      await fetch(this.config.remoteEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(entry),
      });
    } catch (error) {
      // Não logar erro de envio para evitar loop infinito
    }
  }

  debug(message: string, data?: unknown, component?: string, userId?: string) {
    this.log("debug", message, data, component, userId);
  }

  info(message: string, data?: unknown, component?: string, userId?: string) {
    this.log("info", message, data, component, userId);
  }

  warn(message: string, data?: unknown, component?: string, userId?: string) {
    this.log("warn", message, data, component, userId);
  }

  error(message: string, data?: unknown, component?: string, userId?: string) {
    this.log("error", message, data, component, userId);
  }

  // Método para logs que devem aparecer apenas em desenvolvimento
  dev(message: string, data?: unknown, component?: string, userId?: string) {
    if (this.isDevelopment) {
      this.log("info", message, data, component, userId);
    }
  }

  // Método para logs de performance
  performance(
    operation: string,
    duration: number,
    component?: string,
    userId?: string,
  ) {
    this.log(
      "info",
      `Performance: ${operation} took ${duration}ms`,
      { operation, duration },
      component,
      userId,
    );
  }

  // Método para logs de segurança
  security(event: string, data?: unknown, component?: string, userId?: string) {
    this.log("warn", `Security: ${event}`, data, component, userId);
  }

  // Método para obter logs do buffer
  getLogs(): LogEntry[] {
    return [...this.logBuffer];
  }

  // Método para limpar buffer
  clearLogs(): void {
    this.logBuffer = [];
  }

  // Método para configurar o logger
  configure(config: Partial<LogConfig>): void {
    this.config = { ...this.config, ...config };
  }

  // Método para obter estatísticas
  getStats(): { totalLogs: number; sessionId: string; logLevel: LogLevel } {
    return {
      totalLogs: this.logBuffer.length,
      sessionId: this.sessionId,
      logLevel: this.config.logLevel,
    };
  }
}

export const logger = new Logger();

// Hook para usar o logger em componentes React
export const useLogger = (componentName: string) => {
  const debug = useCallback(
    (message: string, data?: unknown, userId?: string) => {
      logger.debug(message, data, componentName, userId);
    },
    [componentName],
  );

  const info = useCallback(
    (message: string, data?: unknown, userId?: string) => {
      logger.info(message, data, componentName, userId);
    },
    [componentName],
  );

  const warn = useCallback(
    (message: string, data?: unknown, userId?: string) => {
      logger.warn(message, data, componentName, userId);
    },
    [componentName],
  );

  const error = useCallback(
    (message: string, data?: unknown, userId?: string) => {
      logger.error(message, data, componentName, userId);
    },
    [componentName],
  );

  const dev = useCallback(
    (message: string, data?: unknown, userId?: string) => {
      logger.dev(message, data, componentName, userId);
    },
    [componentName],
  );

  const performance = useCallback(
    (operation: string, duration: number, userId?: string) => {
      logger.performance(operation, duration, componentName, userId);
    },
    [componentName],
  );

  const security = useCallback(
    (event: string, data?: unknown, userId?: string) => {
      logger.security(event, data, componentName, userId);
    },
    [componentName],
  );

  return {
    debug,
    info,
    warn,
    error,
    dev,
    performance,
    security,
  };
};
